import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { Fish, Waves } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";
import { signInSchema, signUpSchema, SignInFormData, SignUpFormData } from "@/lib/validations";

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const signInForm = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const signUpForm = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: "",
      password: "",
      anglerName: "",
    },
  });

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSignUp = async (values: SignUpFormData) => {
    setLoading(true);

    try {
      const { error, data } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            angler_name: values.anglerName,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        trackEvent({ eventName: AnalyticsEvents.USER_SIGNUP });
        toast.success("Account created! Welcome aboard!");
        navigate("/dashboard");
      }
    } catch (error: any) {
      toast.error(error.message || "Error creating account");
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (values: SignInFormData) => {
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) throw error;

      trackEvent({ eventName: AnalyticsEvents.USER_LOGIN });
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Error signing in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 wave-gradient relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" aria-hidden="true">
        <Waves className="absolute top-10 left-10 w-32 h-32 animate-wave text-primary" />
        <Fish className="absolute bottom-20 right-20 w-24 h-24 animate-wave text-accent" style={{ animationDelay: "1s" }} />
        <Waves className="absolute top-40 right-40 w-20 h-20 animate-wave text-primary" style={{ animationDelay: "2s" }} />
      </div>

      <main id="main-content" tabIndex={-1} role="main" aria-label="Authentication">
        <Card className="w-full max-w-md relative z-10 shadow-2xl">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4" aria-hidden="true">
              <div className="relative">
                <Fish className="h-16 w-16 text-primary" />
                <div className="absolute -top-2 -right-2 h-6 w-6 bg-accent rounded-full animate-ripple" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-gradient">Fishing Weights</CardTitle>
            <CardDescription>Record and verify your trophy catches</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6" role="tablist" aria-label="Authentication options">
                <TabsTrigger value="signin" role="tab">Sign In</TabsTrigger>
                <TabsTrigger value="signup" role="tab">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin" role="tabpanel" aria-labelledby="signin-tab">
                <Form {...signInForm}>
                  <form onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-4" aria-label="Sign in form">
                    <FormField
                      control={signInForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="angler@example.com"
                              autoComplete="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signInForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              autoComplete="current-password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full ocean-gradient" 
                      disabled={loading}
                      aria-label={loading ? "Signing in, please wait" : "Sign in to your account"}
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="signup" role="tabpanel" aria-labelledby="signup-tab">
                <Form {...signUpForm}>
                  <form onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-4" aria-label="Sign up form">
                    <FormField
                      control={signUpForm.control}
                      name="anglerName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Angler Name</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder="Your name"
                              autoComplete="name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signUpForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="angler@example.com"
                              autoComplete="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={signUpForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Minimum 6 characters"
                              autoComplete="new-password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full ocean-gradient" 
                      disabled={loading}
                      aria-label={loading ? "Creating account, please wait" : "Create new account"}
                    >
                      {loading ? "Creating account..." : "Create Account"}
                    </Button>
                  </form>
                </Form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}